// ResearchPublicationsBox.jsx
// Box-only layout • 3 cards per row • Local background image (VISIBLE)

import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";

// ✅ LOCAL IMAGE (must be inside src/)
import bgImage from "../assets/backtgch.png";

const DATA = [
  {
    type: "White Papers",
    title: "FLUDEE Device Overview",
    desc: "System overview, safety, and clinical workflow design.",
    year: "2026",
    tags: ["FLUDEE", "Safety"],
    pdf: "#",
  },
  {
    type: "Feasibility Studies",
    title: "Pilot Study Setup (Hospital)",
    desc: "Pilot methodology, protocol and preliminary observations.",
    year: "2025",
    tags: ["Pilot", "Validation"],
    pdf: "#",
  },
  {
    type: "Abstracts",
    title: "Signal Robustness in Bedside Monitoring",
    desc: "Motion artifact handling and reliability improvements.",
    year: "2025",
    tags: ["Signal", "Reliability"],
    link: "#",
  },
  {
    type: "Posters",
    title: "Usability & Human Factors Poster",
    desc: "UI safety, training guidance, and workflow optimization.",
    year: "2024",
    tags: ["Usability", "Human Factors"],
    pdf: "#",
  },
  {
    type: "Conference Presentations",
    title: "Clinical Validation Strategy Talk",
    desc: "Validation roadmap with hospitals and data safety approach.",
    year: "2024",
    tags: ["Clinical", "Data Safety"],
    link: "#",
  },
  {
    type: "Ongoing Research Work",
    title: "Multi-site Validation Planning",
    desc: "Ongoing collaboration planning with institutions.",
    year: "2026",
    tags: ["Ongoing", "Hospitals"],
  },
];

const iconByType = (type) => {
  const sx = { fontSize: 20 };
  switch (type) {
    case "White Papers":
      return <ArticleOutlinedIcon sx={sx} />;
    case "Feasibility Studies":
      return <AutoGraphOutlinedIcon sx={sx} />;
    case "Conference Presentations":
      return <GroupsOutlinedIcon sx={sx} />;
    default:
      return <ScienceOutlinedIcon sx={sx} />;
  }
};

export default function ResearchPublicationsBox() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        py: { xs: 6, md: 9 },
        minHeight: "80vh",

        // ✅ BACKGROUND IMAGE + LIGHT OVERLAY (IMAGE WILL BE VISIBLE)
        backgroundImage: `
          linear-gradient(
            180deg,
            rgba(255,255,255,0.60),
            rgba(255,255,255,0.85)
          ),
          url(${bgImage})
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Stack alignItems="center" textAlign="center" spacing={1.5} mb={5}>
         

          <Typography variant={isMobile ? "h4" : "h3"} fontWeight={900}>
            Research & Publications
          </Typography>

          <Typography
            sx={{
              maxWidth: 840,
              color: "text.secondary",
              fontSize: { xs: 14, sm: 15.5 },
            }}
          >
            White papers, feasibility studies, abstracts, posters, conference
            presentations, and ongoing research activities.
          </Typography>

       
        </Stack>

        {/* 🔥 BOX ONLY FLEX LAYOUT */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            justifyContent: "center",
          }}
        >
          {DATA.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                width: {
                  xs: "100%",   // 1 per row (mobile)
                  sm: "48%",    // 2 per row (tablet)
                  md: "31.5%",  // 3 per row (desktop)
                },
              }}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  textAlign: "center",
                  bgcolor: "#fff",
                  border: `1px solid ${alpha("#000", 0.06)}`,
                  boxShadow: "0 18px 40px rgba(0,0,0,0.08)",
                  transition: "all .3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 26px 60px rgba(0,0,0,0.14)",
                  },
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={1} justifyContent="center" mb={1}>
                    <Chip
                      icon={iconByType(item.type)}
                      label={item.type}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      }}
                    />
                    <Chip label={item.year} size="small" variant="outlined" />
                  </Stack>

                  <Typography variant="h6" fontWeight={900}>
                    {item.title}
                  </Typography>

                  <Typography mt={1} color="text.secondary" fontSize={14}>
                    {item.desc}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    flexWrap="wrap"
                    mt={2}
                  >
                    {item.tags?.map((t, i) => (
                      <Chip key={i} size="small" label={t} variant="outlined" />
                    ))}
                  </Stack>

                  {(item.pdf || item.link) && (
                    <Stack direction="row" spacing={1} justifyContent="center" mt={2.5}>
                      {item.pdf && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          sx={{
                            borderRadius: 99,
                            fontWeight: 800,
                            textTransform: "none",
                          }}
                        >
                          Download PDF
                        </Button>
                      )}
                      {item.link && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          sx={{
                            borderRadius: 99,
                            fontWeight: 800,
                            textTransform: "none",
                          }}
                        >
                          View Link
                        </Button>
                      )}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
